import { LockClosedIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  DocumentUploadState,
  DocumentUploadZone,
} from '@/components/documents/DocumentUploadZone';
import { FileTtlSelect } from '@/components/FileTtlSelect';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WizardModalFooter, WizardStepper } from '@/components/wizards';
import { useWizardFooterConfig } from '@/components/wizards';
import { useDeleteFile } from '@/hooks';
import {
  FileTtl,
  useEagerUploadFile,
  useSetFileRetention,
} from '@/hooks/useFiles';
import { cn } from '@/lib/utils';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';

export interface UploadFileWizardProps {
  /** Externally controlled open state (the header/empty-state renders its own trigger). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Base prefix for the upload path, e.g. 'home' (FilesTab passes currentFolder.query). */
  query: string;
  /**
   * Tenant email when a Praetorian user is impersonating a customer. Reserved /
   * currently unused: the upload hook is driven by the auth context
   * (`useEagerUploadFile(isImpersonating ? friend : undefined)`), not this prop.
   * Kept on the public API since the page passes it; do NOT thread it into the
   * upload call without confirming it won't double-target the tenant.
   */
  customerEmail?: string;
}

type Visibility = 'internal' | 'shared';

/**
 * Upload sub-machine, independent of the wizard step. Tracks the eager upload
 * that fires the moment a file is dropped.
 *
 *   idle       — no file chosen yet
 *   uploading  — bytes in flight; uploadProgress is a number (or null = indeterminate)
 *   uploaded   — eager upload resolved ✓; name captured; ttl not yet applied
 *   error      — eager upload failed; allow re-drop / retry
 *   confirming — final confirm in progress (apply retention -> invalidate)
 *   done       — confirmed; modal closing
 */
type UploadPhase =
  | 'idle'
  | 'uploading'
  | 'uploaded'
  | 'error'
  | 'confirming'
  | 'done';

interface VisibilityOption {
  value: Visibility;
  title: string;
  desc: string;
  Icon: typeof LockClosedIcon;
}

const OPTIONS: readonly VisibilityOption[] = [
  {
    value: 'internal',
    title: 'Private',
    desc: 'Visible to the Praetorian team only',
    Icon: LockClosedIcon,
  },
  {
    value: 'shared',
    title: 'Public',
    desc: 'Visible to the customer',
    Icon: UsersIcon,
  },
];

// Step arrays per role. Praetorian: Visibility → Files → Retention.
// Customer: Files → Retention (visibility is forced 'shared'). Retention is the
// LAST step for both roles; the eager upload fires on the Files step.
const PRAETORIAN_STEPS = [
  { id: 'visibility', title: 'Visibility' },
  { id: 'files', title: 'Files' },
  { id: 'retention', title: 'Retention' },
] as const;
const CUSTOMER_STEPS = [
  { id: 'files', title: 'Files' },
  { id: 'retention', title: 'Retention' },
] as const;

// Numeric day count per TTL for the Auto-delete helper line ("Deletes
// automatically N days after upload."). Keyed by the FileTtl enum.
const TTL_HELPER_DAYS: Record<FileTtl, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '90d': 90,
};

/**
 * Builds the upload mutation props (name + praetorian + ttl) from the chosen
 * visibility and the user's role. The three paths intentionally diverge per the
 * fixed backend contract:
 *   - Praetorian internal  → `${query}/internal/<file>`     praetorian:true,  no ttl
 *   - Praetorian shared    → `home/shared-with/<file>`      praetorian:false, ttl?
 *   - Customer (shared-by) → `home/shared-by/<file>`        praetorian:false, ttl?
 * Do NOT "fix" the shared paths to use `query` — they are literal by contract.
 */
function uploadTarget(
  visibility: Visibility | undefined,
  isCustomer: boolean,
  query: string,
  fileName: string,
  ttl: FileTtl | undefined
): { name: string; praetorian?: boolean; ttl?: FileTtl } {
  if (isCustomer) {
    return {
      name: `home/shared-by/${fileName}`,
      praetorian: false,
      ...(ttl ? { ttl } : {}),
    };
  }
  if (visibility === 'internal') {
    return {
      name: `${query}/internal/${fileName}`,
      praetorian: true,
    };
  }
  // Praetorian shared
  return {
    name: `home/shared-with/${fileName}`,
    praetorian: false,
    ...(ttl ? { ttl } : {}),
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

/**
 * UploadFileWizard — externally-controlled optimistic upload modal. The byte
 * transfer happens in the background the moment a file is dropped (eager
 * upload), so the final "Upload" click is just a metadata + cache-refresh
 * confirm and feels instant. Cancel / dismissal rolls back by deleting the
 * eagerly-uploaded object.
 *
 * Flow: Praetorian = Visibility → Dropzone(eager upload) → Retention(last).
 * Customer = Dropzone(eager) → Retention(last); visibility forced 'shared'.
 */
export function UploadFileWizard({
  open,
  onOpenChange,
  query,
}: UploadFileWizardProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {/* key={String(open)} fully resets wizard state each time it reopens AND
            unmounts the form on every close — the rollback cleanup hooks into
            that unmount. */}
        <UploadFileWizardForm
          key={String(open)}
          query={query}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function UploadFileWizardForm({
  query,
  onClose,
}: {
  query: string;
  onClose: () => void;
}) {
  const { userType, isImpersonating, friend } = useAuth();
  const isCustomer = userType === 'customer' || userType === 'sso-customer';
  const accountEmail = isImpersonating ? friend : undefined;
  const { mutateAsync: eagerUpload } = useEagerUploadFile(accountEmail);
  const { mutateAsync: setRetention } = useSetFileRetention(accountEmail);
  const { mutateAsync: deleteFile } = useDeleteFile(accountEmail);

  const STEPS = isCustomer ? CUSTOMER_STEPS : PRAETORIAN_STEPS;

  // Both flows start on step 1.
  const [step, setStep] = useState<number>(1);
  const [visibility, setVisibility] = useState<Visibility | undefined>(
    isCustomer ? 'shared' : undefined
  );
  const [phase, setPhase] = useState<UploadPhase>('idle');
  // The `name` from uploadTarget(...).name captured at drop — the rollback
  // identity. null until a file is dropped.
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  // Captured with uploadedName (needed for delete + set-retention; internal
  // uploads are praetorian:true).
  const [uploadedPraetorian, setUploadedPraetorian] = useState<boolean>(false);
  const [fileMeta, setFileMeta] = useState<
    { content: ArrayBuffer; file: File } | undefined
  >();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // Contextual reason from the failed eager upload, shown in the error row.
  // undefined falls back to DocumentUploadZone's generic copy.
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [ttl, setTtl] = useState<FileTtl | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const radiogroupRef = useRef<HTMLDivElement | null>(null);

  // Rollback inputs, read by the once-on-unmount cleanup. Kept current each
  // render so the cleanup always deletes the LATEST eager object (handles
  // re-drop supersede without a double-delete).
  const rollbackRef = useRef<{ name: string | null; praetorian: boolean }>({
    name: null,
    praetorian: false,
  });
  rollbackRef.current = { name: uploadedName, praetorian: uploadedPraetorian };

  // The outer Dialog uses key={String(open)}, so this form unmounts on every
  // close path (footer Cancel, Esc, X, outside-click). All dismissal paths
  // route through onClose -> remount -> this cleanup, which performs exactly
  // one best-effort rollback delete of the eager object. List invalidation was
  // suppressed during the eager upload, so the list never showed the object —
  // no post-delete invalidation is needed. The 7d safety-net TTL (requested at
  // eager-presign) covers deletes that don't land (tab close / nav). After a
  // successful confirm, uploadedName is cleared to null so the kept file is
  // never deleted.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      const { name, praetorian } = rollbackRef.current;
      if (name) {
        // Best-effort rollback; the 7d safety-net tag is the backstop. Swallow
        // rejection so a failed background DELETE isn't an unhandled rejection.
        void deleteFile({ name, praetorian }).catch(() => {});
      }
    },
    // Run once on unmount; values come from rollbackRef. deleteFile is stable.
    []
  );

  const isLastStep = step === STEPS.length;
  const visibilityStepActive = !isCustomer && step === 1;
  const dropzoneStepActive = isCustomer ? step === 1 : step === 2;
  const retentionStepActive = step === STEPS.length;

  function moveSelection(delta: number) {
    const root = radiogroupRef.current;
    if (!root) return;
    const cards = Array.from(
      root.querySelectorAll<HTMLElement>('[role="radio"]')
    );
    const currentIndex = OPTIONS.findIndex(o => o.value === visibility);
    const base = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (base + delta + OPTIONS.length) % OPTIONS.length;
    setVisibility(OPTIONS[nextIndex].value);
    cards[nextIndex]?.focus();
  }

  // Fires the instant a file is dropped (the "instant" mechanism). Captures the
  // name/praetorian for rollback, then eager-uploads WITHOUT invalidating the
  // list. SECURITY C5/C6/C8: shared paths request a 7d safety-net TTL so an
  // abandoned object self-expires; the internal path carries no ttl by contract
  // (uploadTarget) and relies on cleanup-delete.
  async function startEagerUpload(dropped: {
    content: ArrayBuffer;
    file: File;
  }) {
    // Supersede a prior eager upload (re-drop): best-effort delete the old
    // object, abort any in-flight transfer, then start fresh.
    if (uploadedName) {
      void deleteFile({
        name: uploadedName,
        praetorian: uploadedPraetorian,
      }).catch(() => {});
    }
    abortRef.current?.abort();

    const target = uploadTarget(
      visibility,
      isCustomer,
      query,
      dropped.file.name,
      ttl
    );
    const ac = new AbortController();
    abortRef.current = ac;
    setFileMeta(dropped);
    setUploadedName(target.name);
    setUploadedPraetorian(target.praetorian ?? false);
    setUploadProgress(0);
    setErrorMessage(undefined); // clear any prior failure on a fresh attempt
    setPhase('uploading');
    try {
      await eagerUpload({
        ...target,
        // Safety-net 7d TTL for shared paths only. The internal target carries
        // no ttl + praetorian:true; do NOT force a ttl onto it (the backend
        // internal path ignores it).
        ...(target.praetorian === true ? {} : { ttl: '7d' as FileTtl }),
        content: dropped.content,
        signal: ac.signal,
        ignoreSnackbar: true,
        onProgress: e => {
          const pct = (e.loaded / (e.total ?? 1)) * 100;
          setUploadProgress(pct >= 100 ? null : pct);
        },
      });
      setUploadProgress(null);
      setPhase('uploaded');
    } catch (err) {
      if (ac.signal.aborted) return; // superseded / cancelled — not an error
      setUploadProgress(null);
      setUploadedName(null); // single-PUT failed -> no S3 object to roll back
      setErrorMessage(err instanceof Error ? err.message : undefined);
      setPhase('error');
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
    }
  }

  // Clears the current eager upload back to idle (Replace / Remove). Issues the
  // rollback delete for the already-uploaded object, then resets state so a new
  // file can be dropped.
  function handleRemove() {
    abortRef.current?.abort();
    if (uploadedName) {
      void deleteFile({
        name: uploadedName,
        praetorian: uploadedPraetorian,
      }).catch(() => {});
    }
    setUploadedName(null);
    setUploadedPraetorian(false);
    setFileMeta(undefined);
    setUploadProgress(null);
    setPhase('idle');
  }

  // Final confirm: apply the chosen retention to the already-uploaded object,
  // broad-invalidate the file list (reveals the file — the "instant trick"),
  // then close. Clearing uploadedName BEFORE onClose is critical so the unmount
  // cleanup does NOT delete the kept file.
  async function handleConfirm() {
    if (!uploadedName) return;
    setPhase('confirming');
    try {
      const isInternal = visibility === 'internal';
      // Shared paths always reconcile the eager 7d safety-net tag into the user's
      // real choice (incl. clearing it for "keep forever"). Internal uploads carry
      // no safety-net tag, so only call when the user actively picked an expiry;
      // internal + forever (ttl undefined) needs no tag at all. praetorian:true on
      // the internal path makes the PATCH endpoint flip to the praetorian partition.
      if (!isInternal || ttl !== undefined) {
        await setRetention({
          name: uploadedName,
          ttl,
          praetorian: uploadedPraetorian,
        });
      }
      queryClient.invalidateQueries({
        predicate: q => q.queryKey[0] === 'MY' && q.queryKey[1] === 'file',
      });
      // Clear the rollback identity via the ref DIRECTLY — not via React state.
      // onClose() flips the outer Dialog's key={String(open)}, which replaces
      // this fiber; the component body does NOT re-run, so a pending
      // setUploadedName(null) would be discarded and the unmount cleanup would
      // still read the non-null name and delete the file the user just kept.
      // The ref survives the unmount, so clearing it here is what actually
      // prevents the delete.
      rollbackRef.current = { name: null, praetorian: false };
      setUploadedName(null); // keep for in-render UI; no longer load-bearing for rollback
      setPhase('done');
      onClose();
    } catch {
      // Bytes are valid; only the retention tag failed. The hook toasts. Stay
      // open for retry. rollbackRef.current is intentionally left set, so if the
      // user dismisses instead of retrying, the unconfirmed upload is cleaned up
      // on dismissal (handleCancel's awaited delete, or the unmount cleanup +
      // 7d safety-net tag) rather than left behind.
      setPhase('uploaded');
    }
  }

  // Cancel: explicit user dismissal. SECURITY C9: await the rollback delete and
  // surface a failure so a live, retention-tagged object isn't silently left
  // behind. Clear rollbackRef.current FIRST (same mechanism as the confirm fix)
  // so the remount -> unmount cleanup sees a null name and does NOT re-fire —
  // single, awaited delete. (The X / Esc / click-away / tab-close paths stay
  // best-effort via the unmount cleanup + 7d safety-net tag; awaiting is
  // impossible there.)
  async function handleCancel() {
    abortRef.current?.abort();
    const { name, praetorian } = rollbackRef.current;
    rollbackRef.current = { name: null, praetorian: false };
    if (name) {
      try {
        await deleteFile({ name, praetorian });
      } catch {
        // Don't swallow — surface it. The object will auto-expire via the 7d tag.
        toast.error(
          "Couldn't discard the upload — it will auto-expire within 7 days."
        );
      }
    }
    onClose();
  }

  const isPending = phase === 'uploading' || phase === 'confirming';
  const canGoNext = visibilityStepActive
    ? visibility !== undefined
    : dropzoneStepActive
      ? phase === 'uploaded'
      : true;
  const canGoBack = step > 1 && !isPending;

  const footerConfig = useWizardFooterConfig({
    canGoNext,
    canGoBack,
    isPending,
    isLastStep,
    onNext: () => setStep(s => s + 1),
    onBack: () => setStep(s => s - 1),
    onCancel: handleCancel,
    onSubmit: handleConfirm,
    labels: { submit: 'Add file', submitting: 'Adding…' },
  });

  const dropzoneSubtitle =
    visibility === 'internal'
      ? 'Upload internal documents (not visible to customers)'
      : isCustomer
        ? 'Shared with Praetorian for analysis'
        : 'Upload documents to share with the customer';

  // Map the upload phase onto the DocumentUploadZone state machine. confirming/
  // done keep showing the uploaded row.
  const zoneState: DocumentUploadState =
    phase === 'uploading'
      ? 'uploading'
      : phase === 'error'
        ? 'error'
        : phase === 'idle'
          ? 'idle'
          : 'uploaded';

  const fileSizeLabel = fileMeta ? formatBytes(fileMeta.file.size) : undefined;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add file</DialogTitle>
        <DialogDescription>
          {visibilityStepActive
            ? 'Choose who can see this file.'
            : dropzoneStepActive
              ? 'Drop a file — it starts uploading right away.'
              : 'Choose how long to keep this file.'}
        </DialogDescription>
      </DialogHeader>

      {/* Stepper: Praetorian shows 3 dots. Customers (2 steps) hide it — a
          two-dot indicator is noise per the design spec. */}
      {!isCustomer && (
        <WizardStepper
          steps={STEPS.map(s => ({ id: s.id, title: s.title }))}
          currentStep={step}
          variant="dots"
        />
      )}

      <div className="py-2">
        {visibilityStepActive && (
          <div
            ref={radiogroupRef}
            role="radiogroup"
            aria-label="File visibility"
            className="flex flex-col gap-3"
          >
            {OPTIONS.map((opt, i) => {
              const selected = visibility === opt.value;
              const isTabStop =
                selected || (visibility === undefined && i === 0);
              const Icon = opt.Icon;
              return (
                <Card
                  key={opt.value}
                  role="radio"
                  aria-checked={selected}
                  aria-label={opt.title}
                  tabIndex={isTabStop ? 0 : -1}
                  onClick={() => setVisibility(opt.value)}
                  onKeyDown={e => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setVisibility(opt.value);
                    } else if (
                      e.key === 'ArrowDown' ||
                      e.key === 'ArrowRight'
                    ) {
                      e.preventDefault();
                      moveSelection(1);
                    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                      e.preventDefault();
                      moveSelection(-1);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 p-4 transition-colors',
                    'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
                    selected
                      ? 'border-chariot-brand-primary ring-chariot-brand-primary ring-1'
                      : 'border-chariot-stroke'
                  )}
                >
                  <span className="bg-chariot-brand-tertiary text-chariot-brand-primary flex size-8 shrink-0 items-center justify-center rounded-md">
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="text-chariot-text-primary block text-sm font-semibold">
                      {opt.title}
                    </span>
                    <span className="text-chariot-text-secondary block text-xs">
                      {opt.desc}
                    </span>
                  </span>
                </Card>
              );
            })}
          </div>
        )}

        {dropzoneStepActive && (
          <DocumentUploadZone
            onFilesDrop={files =>
              void startEagerUpload(
                files[0] as { content: ArrayBuffer; file: File }
              )
            }
            uploadProgress={uploadProgress}
            state={zoneState}
            fileName={fileMeta?.file.name}
            fileSizeLabel={fileSizeLabel}
            errorMessage={errorMessage}
            subTitle={dropzoneSubtitle}
            ariaLabel="File upload"
            onCancel={handleCancel}
            onRetry={() => fileMeta && void startEagerUpload(fileMeta)}
            onRemove={handleRemove}
          />
        )}

        {retentionStepActive && (
          <div className="flex flex-col gap-4">
            {/* Carry-over reassurance: the uploaded ✓ row stays visible while
                the user picks retention. */}
            <DocumentUploadZone
              onFilesDrop={() => {}}
              uploadProgress={null}
              state={zoneState}
              fileName={fileMeta?.file.name}
              fileSizeLabel={fileSizeLabel}
            />
            <div className="border-chariot-stroke bg-chariot-background-tertiary rounded-md border p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span
                  id="auto-delete-label"
                  className="text-chariot-text-secondary text-xs font-semibold tracking-wide uppercase"
                >
                  Auto-delete
                </span>
                <FileTtlSelect
                  variant="segmented"
                  labelId="auto-delete-label"
                  value={ttl}
                  onChange={setTtl}
                />
              </div>
              <p className="text-chariot-text-secondary mt-2 text-sm">
                {ttl === undefined
                  ? 'Kept until you delete it.'
                  : `Deletes automatically ${TTL_HELPER_DAYS[ttl]} days after upload.`}
              </p>
            </div>
          </div>
        )}
      </div>

      <WizardModalFooter config={footerConfig} />
    </>
  );
}
